import { AfterViewInit, ChangeDetectorRef, Component, HostListener, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, Subscription } from 'rxjs';
import {
  WorkflowRuntimeAuditEvent,
  WorkflowRuntimeDefinition,
  WorkflowRuntimeInstance,
  WorkflowRuntimeOutput,
  WorkflowService
} from 'src/app/services/workflow/workflow.service';
import { GUEST_LINK_TOKEN_KEY, clearGuestSession } from 'src/app/services/workflow/guest-session';
import { evaluateConditions } from 'src/app/components/settings/workflows/workflow-condition-eval';

// RT-32: a note rendered in the item's activity history.
export interface WorkflowItemNote {
  author: string;
  text: string;
  timestampUtc: string;
}

// RT-09 (last AC): an escalation event rendered in the item's workflow history for auditors —
// the recipient it was escalated to, the stage, and when it occurred.
export interface WorkflowEscalationEvent {
  recipient: string;
  stageNumber: number | null;
  timestampUtc: string;
}

// #8: a single entry in the item's full activity history — every workflow event in
// chronological order (submitted, approved, returned, rejected, completed, escalated,
// notes), not just the last state.
export interface WorkflowActivityEvent {
  action: string;
  label: string;
  actor: string;
  detail: string | null;
  stageNumber: number | null;
  timestampUtc: string;
}
import { LogSheetFormComponent } from '../home/log-sheet-form/log-sheet-form.component';
import { ErrorHandlerService } from 'src/app/services/errorHandler/error-handler.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { RefreshStateService } from 'src/app/services/refresh-state/refresh-state.service';

interface WorkflowRefreshSnapshot {
  values: Record<string, any>;
  fieldNotes: Record<string, string>;
  reason: string;
}

@Component({
  standalone: false,
  selector: 'app-workflow-runtime-form',
  templateUrl: './workflow-runtime-form.component.html',
  styleUrls: ['./workflow-runtime-form.component.css']
})
export class WorkflowRuntimeFormComponent implements OnInit, AfterViewInit, OnDestroy {
  mode: 'definition' | 'instance' = 'definition';
  isGuest = false;
  definitionId = 0;
  workflowItemId = 0;
  workflow: WorkflowRuntimeDefinition | WorkflowRuntimeInstance | null = null;
  runtimeItem: WorkflowRuntimeInstance | null = null;
  runtimeDefinition: any = null;
  currentStage: any = null;
  stageNumberToRender = 1;
  stageNumber = 1;
  fields: any[] = [];
  runtimeSections: any[] = [];
  values: Record<string, any> = {};
  reason = '';
  loading = false;
  saving = false;
  errorMessage = '';
  validationErrors: string[] = [];
  // TESTER-2: submission guard duplicate warning is surfaced in a modal.
  showDuplicateModal = false;
  duplicateWarning = '';
  // RT-13 Save Draft: tracks whether a saved draft is backing this Stage 1 start.
  hasSavedDraft = false;
  draftSaving = false;
  // RT-17: escalation visibility — surfaced from the runtime audit trail (ESCALATED event).
  escalated = false;
  escalatedTarget: string | null = null;
  // RT-09 (last AC): full escalation history (event, recipient, date/time) for auditors.
  escalationHistory: WorkflowEscalationEvent[] = [];
  // RT-32: notes captured against the item, surfaced from the audit trail (NOTE events).
  notes: WorkflowItemNote[] = [];
  // #8: the item's full activity history (all events, chronological).
  activity: WorkflowActivityEvent[] = [];
  // N7: the activity timeline is hidden until the user opens it, and a point can be selected.
  // It is only offered when the item is opened from the History section (?view=history),
  // never while acting on a live stage.
  showActivity = false;
  selectedActivity: number | null = null;
  fromHistory = false;
  pendingOccurrenceId: number | null = null;

  toggleActivity(): void {
    this.showActivity = !this.showActivity;
    if (!this.showActivity) this.selectedActivity = null;
  }

  selectActivity(index: number): void {
    this.selectedActivity = this.selectedActivity === index ? null : index;
  }
  newNote = '';
  noteSaving = false;
  downloadingOutputId: number | null = null;
  regeneratingOutputs = false;
  private routeSubscription?: Subscription;
  private refreshSnapshotTimer?: number;
  private refreshRestoreComplete = false;

  @ViewChild(LogSheetFormComponent) private logSheetForm?: LogSheetFormComponent;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private workflowService: WorkflowService,
    private errorService: ErrorHandlerService,
    public toastService: ToastService,
    private refreshState: RefreshStateService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.isGuest = this.route.snapshot.data?.['guest'] === true;

    if (this.isGuest) {
      this.mode = 'instance';
      this.loadGuestItem();
      return;
    }

    this.routeSubscription = this.route.paramMap.subscribe((params) => {
      this.persistRefreshSnapshot();
      const definitionId = Number(params.get('workflowVersionId'));
      const instanceId = Number(params.get('workflowItemId'));
      this.resetRuntimeState();
      this.fromHistory = this.route.snapshot.queryParamMap?.get('view') === 'history';
      const occ = this.route.snapshot.queryParamMap?.get('occurrenceId');
      this.pendingOccurrenceId = occ ? Number(occ) : null;
      // D3: arrived here from the Share icon on an unstarted workflow — the dialog opens itself.
      this.autoOpenShare = this.route.snapshot.queryParamMap?.get('share') === '1';

      if (definitionId) {
        this.mode = 'definition';
        this.definitionId = definitionId;
        this.loadDefinition(definitionId);
        return;
      }

      if (instanceId) {
        this.mode = 'instance';
        this.workflowItemId = instanceId;
        this.loadInstance(instanceId);
        return;
      }

      this.errorMessage = 'Workflow route is missing an identifier';
    });
    this.refreshSnapshotTimer = window.setInterval(() => this.persistRefreshSnapshot(), 750);
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
  }

  ngOnDestroy(): void {
    this.viewReady = false;
    this.persistRefreshSnapshot();
    if (this.refreshSnapshotTimer !== undefined) window.clearInterval(this.refreshSnapshotTimer);
    this.routeSubscription?.unsubscribe();
  }

  /** Guards the explicit render below: nothing to detect before the view exists or after it goes. */
  private viewReady = false;

  /**
   * Applies state that lands after an `await`. Promises handed back by browser APIs (the
   * clipboard) and by the Keycloak token refresh can settle outside the Angular zone, which
   * left the share dialog showing "Creating link…" until the next click forced a render.
   */
  private applyAsyncState(apply: () => void): void {
    this.ngZone.run(() => {
      apply();
      if (this.viewReady) this.cdr.detectChanges();
    });
  }

  @HostListener('window:beforeunload')
  persistBeforeRefresh(): void {
    this.persistRefreshSnapshot();
  }

  getStageNumberToRender(): number {
    if (this.mode === 'definition') return 1;
    const stageNumber = Number(this.runtimeItem?.currentStageNumber);
    if (Number.isFinite(stageNumber) && stageNumber > 0) {
      return stageNumber;
    }
    return 1;
  }

  private resetRuntimeState(): void {
    this.workflow = null;
    this.runtimeItem = null;
    this.runtimeDefinition = null;
    this.currentStage = null;
    this.stageNumberToRender = 1;
    this.stageNumber = 1;
    this.fields = [];
    this.runtimeSections = [];
    this.values = {};
    this.fieldNotes = {};
    this.reason = '';
    this.loading = false;
    this.saving = false;
    this.errorMessage = '';
    this.validationErrors = [];
    this.hasSavedDraft = false;
    this.draftSaving = false;
    this.escalated = false;
    this.escalatedTarget = null;
    this.escalationHistory = [];
    this.notes = [];
    this.activity = [];
    this.showActivity = false;
    this.selectedActivity = null;
    this.refreshRestoreComplete = false;
    this.shareOpen = false;
    this.manualCopyUrl = null;
  }

  private currentRefreshStateKey(): string {
    const id = this.mode === 'definition' ? this.definitionId : this.workflowItemId;
    return `workflow-runtime:${this.mode}:${id}:stage:${this.stageNumber || 1}`;
  }

  private restoreRefreshSnapshot(): void {
    const snapshot = this.refreshState.load<WorkflowRefreshSnapshot>(this.currentRefreshStateKey());
    if (snapshot && this.isEditable()) {
      this.values = { ...this.values, ...(snapshot.values || {}) };
      this.fieldNotes = { ...this.fieldNotes, ...(snapshot.fieldNotes || {}) };
      this.reason = snapshot.reason || '';
    }
    this.refreshRestoreComplete = true;
  }

  private persistRefreshSnapshot(): void {
    if (!this.refreshRestoreComplete || !this.workflow || !this.isEditable()) return;
    const values = this.logSheetForm?.getCurrentFieldValues() ?? this.values;
    const fieldNotes = this.logSheetForm?.getCurrentFieldNotes() ?? this.fieldNotes;
    this.values = { ...this.values, ...values };
    this.fieldNotes = { ...fieldNotes };
    this.refreshState.save<WorkflowRefreshSnapshot>(this.currentRefreshStateKey(), {
      values: { ...this.values },
      fieldNotes: { ...this.fieldNotes },
      reason: this.reason,
    });
  }

  private clearRefreshSnapshot(): void {
    this.refreshState.remove(this.currentRefreshStateKey());
    this.refreshRestoreComplete = false;
  }

  private isTerminalWorkflow(): boolean {
    if (!this.workflow || this.mode !== 'instance') return false;
    const status = ((this.workflow as WorkflowRuntimeInstance).status || '').toString().trim().toUpperCase();
    return status === 'COMPLETED' || status === 'REJECTED';
  }

  private isActionableWorkflow(): boolean {
    return !!this.workflow && !this.isTerminalWorkflow();
  }

  loadDefinition(id: number): void {
    this.loading = true;
    this.workflowService.getRuntimeDefinition(id).subscribe({
      next: (workflow) => {
        this.workflow = workflow;
        this.runtimeDefinition = this.normalizeDefinition(workflow.definition);
        this.values = {};
        this.stageNumberToRender = 1;
        this.stageNumber = 1;
        this.currentStage = this.stageFromDefinition(this.runtimeDefinition, 1);
        this.rebuildRenderableRows();
        this.loading = false;
        // RT-13 AC-02: pre-fill the Start form from a previously saved draft, if any.
        this.loadDraftIntoForm(id);
      },
      error: (err) => this.handleLoadError(err)
    });
  }

  // RT-13 AC-02: hydrate Stage 1 inputs from the saved draft (best-effort; 404 = none saved).
  private loadDraftIntoForm(workflowVersionId: number): void {
    this.workflowService.getRuntimeDraft(workflowVersionId).subscribe({
      next: (draft) => {
        if (draft) {
          this.values = { ...this.values, ...this.extractValues(draft.payload) };
          this.fieldNotes = {
            ...this.fieldNotes,
            ...(draft.payload?.fieldNotes && typeof draft.payload.fieldNotes === 'object'
              ? draft.payload.fieldNotes
              : {})
          };
          this.hasSavedDraft = true;
        }
        this.restoreRefreshSnapshot();
        this.rebuildRenderableRows();
      },
      error: () => {
        // A missing server draft is expected for a new workflow. A local snapshot may
        // still exist when this load was caused by a browser refresh.
        this.restoreRefreshSnapshot();
        this.rebuildRenderableRows();
      }
    });
  }

  /**
   * A guest never names an item: the session token they hold already scopes them to exactly one,
   * so the server decides which item they get. There is no id in the route to tamper with.
   */
  loadGuestItem(): void {
    this.loading = true;
    this.workflowService.getGuestItem().subscribe({
      next: (workflow) => {
        this.runtimeItem = workflow;
        this.workflow = workflow;
        this.workflowItemId = workflow.workflowItemId;
        this.runtimeDefinition = this.normalizeDefinition(workflow.definition);
        this.stageNumberToRender = this.getStageNumberToRender();
        this.stageNumber = this.stageNumberToRender;
        this.currentStage = this.stageFromDefinition(this.runtimeDefinition, this.stageNumberToRender);
        this.values = this.runtimeValues(workflow);
        this.rebuildRenderableRows();
        this.loading = false;

        if (workflow.isEscalated) {
          this.escalated = true;
          this.escalatedTarget = workflow.escalationRole ?? null;
        }
      },
      error: (err) => {
        this.loading = false;
        if (err?.status === 401) {
          clearGuestSession();
          this.router.navigate(['/workflow/link', this.guestLinkToken() ?? ''], { replaceUrl: true });
          return;
        }
        this.handleLoadError(err);
      }
    });
  }

  private guestLinkToken(): string | null {
    return sessionStorage.getItem(GUEST_LINK_TOKEN_KEY);
  }

  loadInstance(id: number): void {
    this.loading = true;
    this.workflowService.getRuntimeItem(id).subscribe({
      next: (workflow) => {
        this.runtimeItem = workflow;
        this.workflow = workflow;
        this.runtimeDefinition = this.normalizeDefinition(workflow.definition);
        this.stageNumberToRender = this.getStageNumberToRender();
        this.stageNumber = this.stageNumberToRender;
        this.currentStage = this.stageFromDefinition(this.runtimeDefinition, this.stageNumberToRender);
        this.values = this.runtimeValues(workflow);
        this.restoreRefreshSnapshot();
        this.rebuildRenderableRows();
        this.loading = false;
        // RT-17: the banner reflects the item's CURRENT escalation state (backend flag), not its
        // history — a resolved item that was once escalated must not keep showing the banner.
        if (workflow.isEscalated) {
          this.escalated = true;
          this.escalatedTarget = workflow.escalationRole ?? null;
        }
        this.loadEscalationState(id);
        // D3: the item now exists, so Copy Link is available — honour the intent that brought
        // us here instead of making the user click Share a second time.
        if (this.autoOpenShare) {
          this.autoOpenShare = false;
          this.openShare();
        }
      },
      error: (err) => this.handleLoadError(err)
    });
  }

  // RT-17 + RT-32: read the audit trail once to surface escalation state and the note history.
  private loadEscalationState(workflowItemId: number): void {
    this.workflowService.getRuntimeAuditTrail(workflowItemId).subscribe({
      next: (events) => {
        this.escalationHistory = this.extractEscalationHistory(events || []);
        this.notes = this.extractNotes(events || []);
        this.activity = this.buildActivity(events || []);
      },
      error: () => { /* audit unavailable → no escalation banner / notes / activity */ }
    });
  }

  // RT-09 (last AC): ESCALATED events in chronological order with recipient, stage and timestamp,
  // so an auditor viewing the item's history sees each escalation event, who it went to, and when.
  private extractEscalationHistory(events: WorkflowRuntimeAuditEvent[]): WorkflowEscalationEvent[] {
    return events
      .filter((e) => (e.action || '').toString().trim().toUpperCase() === 'ESCALATED')
      .map((e) => {
        const target = e.details?.escalationTarget;
        return {
          recipient: typeof target === 'string' && target.trim() ? target.trim() : 'Unassigned',
          stageNumber: e.stageNumber ?? null,
          timestampUtc: e.timestampUtc
        };
      });
  }

  // #8: only these audit actions are surfaced as user-facing history (the rest —
  // ACCESSED, OTP/LINK lifecycle, security violations — are audit-only noise).
  private static readonly ACTIVITY_LABELS: Record<string, string> = {
    SUBMITTED: 'Submitted',
    APPROVED: 'Approved',
    RETURNED: 'Returned',
    REJECTED: 'Rejected',
    COMPLETED: 'Completed',
    ESCALATED: 'Escalated',
    NOTE: 'Comment added',
    SLA_BREACHED: 'SLA breached'
  };

  // #8: build the complete, chronological activity history from the audit trail.
  private buildActivity(events: WorkflowRuntimeAuditEvent[]): WorkflowActivityEvent[] {
    const out: WorkflowActivityEvent[] = [];
    for (const e of events) {
      const action = (e.action || '').toString().trim().toUpperCase();
      const label = WorkflowRuntimeFormComponent.ACTIVITY_LABELS[action];
      if (!label) continue;
      out.push({
        action,
        label,
        actor: e.userEmail || 'System',
        detail: this.activityDetail(action, e),
        stageNumber: e.stageNumber ?? null,
        timestampUtc: e.timestampUtc
      });
    }
    return out;
  }

  private activityDetail(action: string, e: WorkflowRuntimeAuditEvent): string | null {
    const details = e.details || {};
    if (action === 'ESCALATED') {
      const target = details.escalationTarget;
      return typeof target === 'string' && target.trim() ? `to ${target.trim()}` : null;
    }
    if (action === 'NOTE') {
      return (details.note ?? '').toString().trim() || null;
    }
    if (action === 'RETURNED' || action === 'REJECTED') {
      return (details.reason ?? '').toString().trim() || null;
    }
    return null;
  }

  // RT-32: NOTE events in chronological order with author + timestamp.
  private extractNotes(events: WorkflowRuntimeAuditEvent[]): WorkflowItemNote[] {
    return events
      .filter((e) => (e.action || '').toString().trim().toUpperCase() === 'NOTE')
      .map((e) => ({
        author: e.userEmail || 'Unknown',
        text: (e.details?.note ?? '').toString(),
        timestampUtc: e.timestampUtc
      }))
      .filter((n) => n.text.trim().length > 0);
  }

  // RT-32: append a note to the item's activity history.
  addNote(): void {
    const text = (this.newNote || '').trim();
    if (this.noteSaving || this.mode !== 'instance' || !this.workflowItemId || text.length === 0) return;
    this.noteSaving = true;
    this.errorMessage = '';
    this.workflowService.addRuntimeNote(this.workflowItemId, text).subscribe({
      next: (event) => {
        this.noteSaving = false;
        this.newNote = '';
        this.notes = [...this.notes, {
          author: event.userEmail || 'You',
          text: (event.details?.note ?? text).toString(),
          timestampUtc: event.timestampUtc
        }];
        this.toastService.showToast = true;
        this.toastService.toastText = 'Comment added';
        setTimeout(() => (this.toastService.showToast = false), 1500);
      },
      error: (err: any) => {
        this.noteSaving = false;
        this.errorMessage = err?.error?.message
          || this.errorService.handleRetrievalErrors(err)?.message
          || 'Note could not be added';
      }
    });
  }

  downloadOutput(output: WorkflowRuntimeOutput): void {
    if (this.downloadingOutputId !== null) return;
    this.downloadingOutputId = output.workflowOutputId;
    this.workflowService.downloadRuntimeOutput(output.workflowOutputId).subscribe({
      next: (response) => {
        this.downloadingOutputId = null;
        const blob = response.body;
        if (!blob) return;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = output.fileName || `workflow-output-${output.workflowOutputId}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
      },
      error: (err: any) => {
        this.downloadingOutputId = null;
        this.errorMessage = err?.error?.message
          || this.errorService.handleRetrievalErrors(err)?.message
          || 'Output could not be downloaded';
      }
    });
  }

  /** Keycloak stores the role as `super_admin`; older sessions used `Super Admin`. */
  private roleMatches(...accepted: string[]): boolean {
    const role = (localStorage.getItem('role') || '').trim().toLowerCase().replace(/[\s-]/g, '_');
    return accepted.includes(role);
  }

  get isSuperAdmin(): boolean {
    return this.roleMatches('super_admin');
  }

  get hasFlaggedOutput(): boolean {
    return (this.runtimeItem?.outputs || []).some(
      (o) => (o.status || '').toString().trim().toUpperCase() === 'FLAGGED'
    );
  }

  get canRegenerateOutputs(): boolean {
    return this.mode === 'instance' && this.isSuperAdmin && this.hasFlaggedOutput;
  }

  regenerateOutputs(): void {
    if (this.regeneratingOutputs || !this.workflowItemId) return;
    this.regeneratingOutputs = true;
    this.errorMessage = '';
    this.workflowService.regenerateRuntimeOutputs(this.workflowItemId).subscribe({
      next: (result) => {
        this.regeneratingOutputs = false;
        this.toastService.showToast = true;
        this.toastService.toastText = result?.message || 'Regeneration queued';
        setTimeout(() => (this.toastService.showToast = false), 2000);
        // Outputs generate on a background job; reload shortly to reflect the new status.
        setTimeout(() => this.loadInstance(this.workflowItemId), 2500);
      },
      error: (err: any) => {
        this.regeneratingOutputs = false;
        this.errorMessage = err?.error?.message
          || this.errorService.handleRetrievalErrors(err)?.message
          || 'Outputs could not be regenerated';
      }
    });
  }

  /* ---- D3: copy a shareable link to this workflow item ---------------------
     The link opens the mobile app when it is installed and the portal otherwise,
     so an admin can hand it to an approver directly instead of waiting on email. */

  linkIssuing = false;
  linkCopied = false;

  /** Seam for tests; the real clipboard is unavailable in a headless run. */
  clipboardWrite: (text: string) => Promise<void> = (text) =>
    navigator.clipboard?.writeText(text) ?? Promise.reject(new Error('No clipboard API'));

  /**
   * Copies without ever hanging. navigator.clipboard.writeText() stays pending forever
   * when the document is not focused (Chrome), so it is raced against a timeout, and an
   * insecure origin has no clipboard at all — hence the execCommand fallback.
   */
  private async copyWithFallback(text: string): Promise<boolean> {
    const outcome = await Promise.race([
      this.clipboardWrite(text).then(() => 'ok' as const).catch(() => 'failed' as const),
      new Promise<'timeout'>((resolve) => setTimeout(() => resolve('timeout'), 1200)),
    ]);
    if (outcome === 'ok') return true;

    const area = document.createElement('textarea');
    area.value = text;
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    try {
      return document.execCommand('copy');
    } catch {
      return false;
    } finally {
      document.body.removeChild(area);
    }
  }

  /** There is nothing to link to until the item exists (so never in definition mode).
   *  Beyond that the API allows administrators and participants in the item — an operator
   *  is often the first/second approver — so anyone who can open the item may share it. */
  get canCopyLink(): boolean {
    return this.mode === 'instance' && !!this.workflowItemId && !this.isGuest;
  }

  get isAdmin(): boolean {
    return this.roleMatches('admin', 'super_admin');
  }

  /** The share dialog: an optional recipient turns the link into a guest link. */
  shareOpen = false;
  /** Set by `?share=1` — the item was provisioned purely so it could be shared. */
  private autoOpenShare = false;
  shareEmail = '';
  /** Set only when the automatic clipboard copy failed, so the guest can copy the URL by hand. */
  manualCopyUrl: string | null = null;

  openShare(): void {
    if (!this.canCopyLink) return;
    this.shareEmail = '';
    this.errorMessage = '';
    this.linkCopied = false;
    this.manualCopyUrl = null;
    this.shareOpen = true;
  }

  closeShare(): void {
    this.shareOpen = false;
    this.manualCopyUrl = null;
  }

  get shareEmailIsValid(): boolean {
    const email = this.shareEmail.trim();
    return email.length === 0 || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  async copyItemLink(): Promise<void> {
    if (this.linkIssuing || !this.canCopyLink || !this.shareEmailIsValid) return;
    this.linkIssuing = true;
    this.linkCopied = false;
    this.errorMessage = '';
    this.manualCopyUrl = null;

    // A recipient is what makes the link openable by someone with no account: the API only
    // offers one-time-code verification when it has an address to send the code to.
    const recipientEmail = this.shareEmail.trim() || null;

    try {
      const link = await firstValueFrom(
        this.workflowService.issueRuntimeLink({
          workflowItemId: this.workflowItemId,
          targetActorRole: this.runtimeItem?.currentActorRole ?? null,
          recipientEmail,
        })
      );

      // The API builds the URL from the environment's host; a null means none is set.
      if (!link?.url) {
        this.applyAsyncState(() => {
          this.errorMessage =
            'No workflow link host is configured for this environment, so a link cannot be created.';
        });
        return;
      }

      // copyWithFallback is bounded by a 1200ms timeout race, so awaiting it can't hang the UI.
      // Only report success once the copy actually lands — otherwise the modal stays open and
      // surfaces the URL for manual copy, so we never falsely claim the link was copied.
      const url = link.url;
      const copied = await this.copyWithFallback(url);
      if (!copied) {
        this.applyAsyncState(() => {
          this.manualCopyUrl = url;
          this.errorMessage =
            'The link was created but could not be copied automatically. Copy it manually below.';
        });
        return;
      }

      this.applyAsyncState(() => {
        this.linkCopied = true;
        this.shareOpen = false;
        this.toastService.showToast = true;
        this.toastService.toastText = recipientEmail
          ? `Guest link copied for ${recipientEmail}`
          : 'Link copied';
      });
      setTimeout(() => this.applyAsyncState(() => (this.toastService.showToast = false)), 2500);
      setTimeout(() => this.applyAsyncState(() => (this.linkCopied = false)), 2500);
    } catch (err: any) {
      this.applyAsyncState(() => {
        this.errorMessage = err?.error?.message
          || this.errorService.handleRetrievalErrors(err)?.message
          || 'The workflow link could not be created.';
      });
    } finally {
      this.applyAsyncState(() => {
        this.linkIssuing = false;
      });
    }
  }

  get isReadOnly(): boolean {
    return this.mode === 'instance' && !this.isEditable();
  }

  // RT-21: the current stage has breached its SLA deadline.
  get isOverdue(): boolean {
    return this.mode === 'instance' && this.runtimeItem?.isOverdue === true && !this.isTerminalWorkflow();
  }

  // RT-21: the current stage's SLA deadline, when a time budget is configured.
  get slaDeadline(): string | null {
    return this.mode === 'instance' ? this.runtimeItem?.slaDeadline ?? null : null;
  }

  submitWorkflow(): void {
    if (!this.validateCurrentStageForSubmit()) return;
    // Auto-generated values (Timestamp/Reference Number/Duration) were stamped during
    // validation; pull them into local state so they ride along in the payload.
    this.collectCurrentFieldValues();

    if (this.mode === 'definition') {
      this.persist(() => this.workflowService.submitWorkflow(this.definitionId, this.payload(), this.pendingOccurrenceId), 'Workflow submitted');
      return;
    }
    if (this.canResubmit()) {
      this.persist(
        () => this.isGuest
          ? this.workflowService.guestResubmit({ payload: this.payload() })
          : this.workflowService.resubmitWorkflow(this.workflowItemId, this.payload()),
        'Workflow resubmitted'
      );
      return;
    }
    // D3: a pending item (never submitted) — canSubmit is true here but canResubmit is not,
    // since that's reserved for rework after a Return/Reject.
    if (this.canSubmit()) {
      this.persist(
        () => this.isGuest
          ? this.workflowService.guestSubmitPendingItem({ payload: this.payload() })
          : this.workflowService.submitPendingItem(this.workflowItemId, this.payload()),
        'Workflow submitted'
      );
    }
  }

  // D3: provisions a blank Stage 1 item from the Start page and opens its instance route, so
  // the familiar Copy Link icon (canCopyLink, instance-mode only) becomes available immediately —
  // a workflow nobody has started yet should still be shareable.
  startingPendingItem = false;

  startAndShareWorkflow(): void {
    if (this.startingPendingItem || this.mode !== 'definition' || !this.definitionId) return;
    this.startingPendingItem = true;
    this.errorMessage = '';
    this.workflowService.startPendingItem(this.definitionId, this.pendingOccurrenceId).subscribe({
      next: (instance) => {
        this.startingPendingItem = false;
        // `share=1` carries the intent across the navigation so the dialog opens on arrival —
        // otherwise the click only swaps the page and the user has to find the icon again.
        this.router.navigate(['/workflow-runtime', 'instances', instance.workflowItemId], {
          queryParams: { share: 1 }
        });
      },
      error: (err: any) => {
        this.startingPendingItem = false;
        this.errorMessage = err?.error?.message
          || this.errorService.handleRetrievalErrors(err)?.message
          || 'The workflow could not be started for sharing.';
      }
    });
  }

  // RT-13 AC-01: persist the in-progress Stage 1 payload without creating a workflow item.
  saveDraft(): void {
    if (this.draftSaving || this.mode !== 'definition' || !this.workflow) return;
    // Drafts are partial by design — collect whatever has been entered, no validation gate.
    this.collectCurrentFieldValues();
    this.draftSaving = true;
    this.errorMessage = '';
    this.workflowService.saveRuntimeDraft(this.definitionId, this.payload()).subscribe({
      next: () => {
        this.draftSaving = false;
        this.hasSavedDraft = true;
        this.toastService.showToast = true;
        this.toastService.toastText = 'Draft saved';
        setTimeout(() => (this.toastService.showToast = false), 1500);
      },
      error: (err: any) => {
        this.draftSaving = false;
        this.errorMessage = err?.error?.message
          || this.errorService.handleRetrievalErrors(err)?.message
          || 'Draft could not be saved';
      }
    });
  }

  // RT-13: discard the saved draft and return to the work list.
  discardDraft(): void {
    if (this.draftSaving || this.mode !== 'definition' || !this.hasSavedDraft) return;
    this.draftSaving = true;
    this.workflowService.deleteRuntimeDraft(this.definitionId).subscribe({
      next: () => {
        this.draftSaving = false;
        this.hasSavedDraft = false;
        this.clearRefreshSnapshot();
        this.toastService.showToast = true;
        this.toastService.toastText = 'Draft discarded';
        setTimeout(() => {
          this.toastService.showToast = false;
          this.router.navigate([this.returnRoute()]);
        }, 800);
      },
      error: (err: any) => {
        this.draftSaving = false;
        this.errorMessage = err?.error?.message
          || this.errorService.handleRetrievalErrors(err)?.message
          || 'Draft could not be discarded';
      }
    });
  }

  canSaveDraft(): boolean {
    return this.mode === 'definition' && this.isActionableWorkflow();
  }

  approveWorkflow(): void {
    this.persist(
      () => this.isGuest
        ? this.workflowService.guestApprove({ payload: this.payload() })
        : this.workflowService.approveWorkflow(this.workflowItemId, { payload: this.payload() }),
      'Workflow approved'
    );
  }

  returnWorkflow(): void {
    this.persist(
      () => this.isGuest
        ? this.workflowService.guestReturn({ reason: this.reason })
        : this.workflowService.returnWorkflow(this.workflowItemId, { reason: this.reason }),
      'Workflow returned'
    );
  }

  rejectWorkflow(): void {
    this.persist(
      () => this.isGuest
        ? this.workflowService.guestReject({ reason: this.reason })
        : this.workflowService.rejectWorkflow(this.workflowItemId, { reason: this.reason }),
      'Workflow rejected'
    );
  }

  // #6: stage actions (approve / return / reject) are confirmed before they commit.
  pendingAction: 'approve' | 'return' | 'reject' | null = null;

  requestAction(action: 'approve' | 'return' | 'reject'): void {
    if (this.saving) return;
    // Reason is now captured inside the modal — open it fresh each time.
    if (action === 'return' || action === 'reject') this.reason = '';
    this.pendingAction = action;
  }

  get pendingActionLabel(): string {
    switch (this.pendingAction) {
      case 'approve': return 'Approve';
      case 'return': return 'Return';
      case 'reject': return 'Reject';
      default: return '';
    }
  }

  // Return and Reject must carry a reason; Approve does not.
  get pendingActionNeedsReason(): boolean {
    return this.pendingAction === 'return' || this.pendingAction === 'reject';
  }

  get canConfirmPendingAction(): boolean {
    if (!this.pendingAction) return false;
    return this.pendingActionNeedsReason ? this.reason.trim().length > 0 : true;
  }

  confirmAction(): void {
    const action = this.pendingAction;
    if (!action || !this.canConfirmPendingAction) return;
    this.pendingAction = null;
    if (action === 'approve') this.approveWorkflow();
    else if (action === 'return') this.returnWorkflow();
    else this.rejectWorkflow();
  }

  cancelAction(): void {
    this.pendingAction = null;
    this.reason = '';
  }

  persist(requestFactory: () => any, successMessage: string): void {
    if (this.saving || !this.workflow) return;
    this.saving = true;
    this.errorMessage = '';
    this.validationErrors = [];
    const request = requestFactory();

    request.subscribe({
      next: () => {
        this.saving = false;
        this.clearRefreshSnapshot();
        this.toastService.showToast = true;
        this.toastService.toastText = successMessage;
        setTimeout(() => {
          this.toastService.showToast = false;
          this.router.navigate([this.returnRoute()]);
        }, 800);
      },
      error: (err: any) => {
        this.saving = false;
        // TESTER-2: a duplicate submission guard conflict (RT-11, HTTP 409) is shown as a modal
        // warning rather than an inline error banner.
        if (err?.status === 409) {
          this.duplicateWarning = err?.error?.message
            || 'A matching submission already exists and is still in progress.';
          this.showDuplicateModal = true;
          return;
        }
        this.validationErrors = this.extractBackendValidationErrors(err);
        // Surface the backend's specific message (e.g. required-field validation) instead of
        // the generic retrieval fallback.
        this.errorMessage = err?.error?.message
          || this.errorService.handleRetrievalErrors(err)?.message
          || 'Workflow could not be saved';
      }
    });
  }

  goBack(): void {
    this.router.navigate([this.returnRoute()]);
  }

  dismissDuplicateModal(): void {
    this.showDuplicateModal = false;
    this.duplicateWarning = '';
  }

  fieldOptions(field: any): string[] {
    return Array.isArray(field.options) ? field.options : [];
  }

  fieldType(field: any): string {
    return (field?.type || '').toString().toLowerCase();
  }

  fieldValue(field: any): any {
    return this.values[field.valueId || field.id] ?? '';
  }

  isLongText(field: any): boolean {
    const type = this.fieldType(field);
    return type === 'longtext' || type === 'longtextinput' || type === 'long_text';
  }

  isText(field: any): boolean {
    const type = this.fieldType(field);
    return type === 'text' || type === 'shorttext' || type === 'short_text';
  }

  isFieldReadonly(field: any): boolean {
    return !!field.readonly || !this.isEditable();
  }

  updateFieldValue(field: any, value: any): void {
    if (this.isFieldReadonly(field)) return;
    this.values[field.valueId || field.id] = value;
  }

  canSubmit(): boolean {
    if (!this.isActionableWorkflow()) return false;
    return this.mode === 'definition' || !!(this.workflow as WorkflowRuntimeInstance).canSubmit;
  }

  canResubmit(): boolean {
    return this.isActionableWorkflow() && this.mode === 'instance' && !!(this.workflow as WorkflowRuntimeInstance | null)?.canResubmit;
  }

  canApprove(): boolean {
    return this.isActionableWorkflow() && this.mode === 'instance' && !!(this.workflow as WorkflowRuntimeInstance | null)?.canApprove;
  }

  canReturn(): boolean {
    return this.isActionableWorkflow() && this.mode === 'instance' && !!(this.workflow as WorkflowRuntimeInstance | null)?.canReturn;
  }

  canReject(): boolean {
    return this.isActionableWorkflow() && this.mode === 'instance' && !!(this.workflow as WorkflowRuntimeInstance | null)?.canReject;
  }

  isEditable(): boolean {
    return this.mode === 'definition' || this.canSubmit() || this.canResubmit() || this.canApprove();
  }

  get returnReason(): string | null {
    return (this.workflow as WorkflowRuntimeInstance | null)?.lastReturnReason ?? null;
  }

  hasAnyAction(): boolean {
    return this.canSubmit() || this.canResubmit() || this.canApprove() || this.canReturn() || this.canReject();
  }

  private hydrateStage(): void {
    this.runtimeDefinition = this.normalizeDefinition(this.runtimeDefinition ?? this.workflow?.definition ?? {});
    this.stageNumberToRender = this.getStageNumberToRender();
    this.stageNumber = this.stageNumberToRender;
    this.currentStage = this.stageFromDefinition(this.runtimeDefinition, this.stageNumberToRender);
    this.rebuildRenderableRows();
  }

  private rebuildRenderableRows(): void {
    this.fields = this.flattenStageFields(this.currentStage, this.runtimeDefinition ?? {});
    for (const field of this.fields) {
      const key = field.valueId || field.id;
      if (this.values[key] === undefined) this.values[key] = this.resolveComputedValue(field);
      if (!field.readonly) {
        field.readonly = !this.isEditable();
      } else {
        field.readonly = !!field.readonly || !this.isEditable();
      }
    }
    this.runtimeSections = this.fields.length > 0 ? [this.buildRuntimeSection(this.fields)] : [];
  }

  private stageFromDefinition(definition: any, stageNumber: number): any {
    return definition?.stages?.[String(stageNumber)] ?? null;
  }

  private normalizeDefinition(definition: any): any {
    if (!definition) return {};
    if (typeof definition === 'string') {
      try {
        return JSON.parse(definition);
      } catch {
        return {};
      }
    }
    return definition.definition?.stages ? definition.definition : definition;
  }

  private flattenStageFields(stage: any, definition: any): any[] {
    const rows = this.regroupLegacyMirrorRows(this.normalizeRows(stage?.rows), definition);
    const sourceFields = this.buildSourceFieldIndex(definition);
    const fields: any[] = [];
    const seen = new Set<string>();
    for (const row of rows) {
      // Keep the config's row grouping so the form renders like the builder.
      let rowStarted = false;
      for (const entry of row ?? []) {
        const field = this.resolveField(entry, sourceFields);
        if (!field?.id) continue;
        // Guard against the same field being placed twice in a stage (mirror + native,
        // or a duplicated row entry) so it doesn't render as a duplicate input.
        const key = (field.valueId || field.id).toString();
        if (seen.has(key)) continue;
        seen.add(key);
        field.rowStart = !rowStarted;
        rowStarted = true;
        fields.push(field);
      }
    }
    return fields;
  }

  private resolveField(entry: any, sourceFields: Record<string, any>): any {
    if (!entry) return null;
    if (entry.kind === 'native') return entry.field;
    if (entry.field?.id) return entry.field;
    if (entry.kind === 'mirror') {
      const source = sourceFields[entry.refId];
      if (!source) {
        return {
          id: `missing_${entry.refId || 'field'}`,
          valueId: entry.refId,
          label: 'Unavailable field',
          type: 'text',
          readonly: true,
          mirror: true,
        };
      }
      return {
        ...source,
        valueId: entry.refId,
        readonly: true,
        mirror: true,
      };
    }
    return entry;
  }

  private regroupLegacyMirrorRows(rows: any[], definition: any): any[] {
    const mirrorRef = (entry: any): string | null =>
      entry?.kind === 'mirror' && entry.refId != null ? entry.refId.toString() : null;

    if (rows.length < 2 || !rows.every((row) => row?.length === 1 && mirrorRef(row[0]))) {
      return rows;
    }
    const stageOneRows = this.normalizeRows(definition?.stages?.['1']?.rows);
    if (stageOneRows.length === 0) return rows;

    const entryByRef = new Map<string, any>();
    for (const row of rows) entryByRef.set(mirrorRef(row[0])!, row[0]);

    const regrouped: any[][] = [];
    const placed = new Set<string>();
    for (const sourceRow of stageOneRows) {
      const group: any[] = [];
      for (const sourceEntry of sourceRow ?? []) {
        const id = this.realField(sourceEntry)?.id?.toString();
        const entry = id ? entryByRef.get(id) : undefined;
        if (!entry) continue;
        group.push(entry);
        placed.add(id!);
      }
      if (group.length > 0) regrouped.push(group);
    }
    for (const row of rows) {
      if (!placed.has(mirrorRef(row[0])!)) regrouped.push(row);
    }
    return regrouped.length > 0 ? regrouped : rows;
  }

  private normalizeRows(rows: any): any[] {
    if (!rows) return [];
    if (Array.isArray(rows)) {
      return rows.map((row) => Array.isArray(row) ? row : [row]);
    }
    if (typeof rows === 'object') {
      return Object.values(rows).map((row) => Array.isArray(row) ? row : [row]);
    }
    return [];
  }

  private buildSourceFieldIndex(definition: any): Record<string, any> {
    const fields: Record<string, any> = {};
    const fallbackFields: Record<string, any> = {};
    for (const stage of Object.values(definition?.stages ?? {}) as any[]) {
      for (const row of this.normalizeRows(stage?.rows)) {
        for (const entry of row ?? []) {
          const field = this.realField(entry);
          if (field?.id && entry?.kind !== 'mirror' && !fields[field.id]) {
            fields[field.id] = field;
          } else if (field?.id && !fallbackFields[field.id]) {
            fallbackFields[field.id] = field;
          }
        }
      }
    }
    return { ...fallbackFields, ...fields };
  }

  private realField(entry: any): any {
    if (!entry) return null;
    if (entry.kind === 'native') return entry.field;
    if (entry.kind === 'mirror') return entry.field || null;
    if (entry.field?.id) return entry.field;
    return entry;
  }

  private handleLoadError(err: any): void {
    this.loading = false;
    if (err?.status === 403 || err?.status === 404) {
      this.workflow = null;
      this.runtimeItem = null;
      this.showAccessDeniedToastAndReturn();
      return;
    }

    this.errorMessage = this.errorService.handleRetrievalErrors(err)?.message || 'Workflow could not be loaded';
  }

  private showAccessDeniedToastAndReturn(): void {
    this.toastService.isError = true;
    this.toastService.showToast = true;
    this.toastService.toastText = "You don't have permissions to access this workflow.";
    this.router.navigate([this.returnRoute()]);
    setTimeout(() => {
      this.toastService.showToast = false;
      this.toastService.isError = false;
      this.toastService.toastText = '';
    }, 5000);
  }

  private payload(): any {
    const values = this.collectCurrentFieldValues();
    const fieldNotes = this.logSheetForm?.getCurrentFieldNotes() ?? {};
    return { fields: values, fieldNotes };
  }

  private collectCurrentFieldValues(): Record<string, any> {
    const fieldValues = this.logSheetForm?.getCurrentFieldValues();
    this.values = fieldValues ? { ...this.values, ...fieldValues } : { ...this.values };
    return { ...this.values };
  }

  /**
   * Re-evaluates every field's Conditional Visibility against the latest entered values and
   * flips `hiddenByDefault` IN PLACE on the log-sheet-form's own data point objects — the same
   * mutation style its legacy showHiddenField/hideHiddenField use. Deliberately does not rebuild
   * or reassign `runtimeSections`: that array has no trackBy, so a fresh array/object graph would
   * make *ngFor tear down and recreate every field's DOM node (losing focus mid-type).
   * Bound to (change)/(focusout)/(click) on the wrapper — see template — rather than legacy
   * triggerConditions call sites, since a visibility condition can reference ANY field in the
   * stage, not only ones that themselves carry a trigger.
   */
  recomputeFieldVisibility(): void {
    this.collectCurrentFieldValues();
    const dataPoints = this.logSheetForm?.sections?.[0]?.dataPoints ?? [];
    for (const dp of dataPoints) {
      if (!dp?.visibilityConditions) continue;
      dp.hiddenByDefault = !evaluateConditions(dp.visibilityConditions, this.values);
    }
  }

  private validateCurrentStageForSubmit(): boolean {
    if (!this.isEditable()) return true;

    // Delegate to the log-sheet renderer so validation is shown INLINE next to each field
    // (instead of a separate list at the top of the form), and so auto-generated fields are
    // stamped + exempted from "required" consistently with the standard log-sheet form.
    const valid = this.logSheetForm?.prepareAndValidateForSubmit() ?? true;
    this.validationErrors = [];
    this.errorMessage = valid ? '' : 'Please complete the highlighted fields before submitting.';
    return valid;
  }

  private extractBackendValidationErrors(err: any): string[] {
    const errors = err?.error?.errors;
    if (!Array.isArray(errors)) return [];
    return errors.map((error: any) => {
      const label = error?.label || error?.fieldId || 'Field';
      const message = error?.message || 'is invalid.';
      return `${label}: ${message}`;
    });
  }

  private buildRuntimeSection(fields: any[]): any {
    // #N2: resolve references (e.g. a Duration field's start/end) by BOTH id and valueId so a
    // reference that targets field.id still maps to the numeric dataPointId. Otherwise the Duration
    // falls back to its self-contained variant (manual start/end) instead of auto-calculating.
    const idMap = new Map<string, number>();
    fields.forEach((field: any, index: number) => {
      const n = index + 1;
      if (field.id) idMap.set(field.id, n);
      if (field.valueId) idMap.set(field.valueId, n);
      idMap.set(`field_${index}`, n);
    });

    const dataPoints = fields.map((field: any, index: number) => {
      const dataPointId = index + 1;
      const dataType = this.mapRuntimeFieldType(field.type);
      const listOptions = this.runtimeFieldListOptions(field, idMap);
      const triggerConditions = this.runtimeTriggerConditions(field, idMap);
      // Auto-generated fields (Reference Number, Timestamp, Duration) are system-computed and
      // never user-fillable, so they must never be marked required.
      const required = !!field.required && !this.isAutoGeneratedType(field.type);
      // Field-level Conditional Visibility (builder's visibility.enabled/conditions) is a
      // separate, richer AND/OR system from the legacy single-condition triggerConditions above
      // (which workflow fields never populate). Evaluated directly here — and reactively via
      // recomputeFieldVisibility() — rather than through the legacy engine, which can't express
      // multi-condition AND/OR rules.
      const visibilityConditions = field.visibility?.enabled ? field.visibility.conditions ?? [] : null;
      const hiddenByDefault = visibilityConditions
        ? !evaluateConditions(visibilityConditions, this.values)
        : !!field.hiddenByDefault;
      return {
        dataPointId,
        dataPointName: field.label || field.id || `Field ${dataPointId}`,
        dataType,
        listOptions,
        hiddenByDefault,
        mandatoryWhenVisible: required,
        requiredWhenVisible: required,
        triggerConditions,
        visibilityConditions,
        visibilityFieldId: field.id,
        readonly: !!field.readonly,
        mirror: !!field.mirror,
        valueId: field.valueId || field.id,
        rowStart: !!field.rowStart,
      };
    });

    const dataPointValues = fields.map((field: any, index: number) => ({
      dataPointId: index + 1,
      value: this.values[field.valueId || field.id] ?? '',
      note: this.fieldNotes[field.valueId || field.id] ?? '',
    }));

    return {
      id: 1,
      name: this.currentStage?.name || `Stage ${this.stageNumber}`,
      dataPoints,
      dataPointValues,
      isSubmitted: false,
      entryId: 1,
    };
  }

  private mapRuntimeFieldType(type: string): string {
    if (!type) return 'Text (Short)';
    const normalized = type.toString().trim().toLowerCase();
    switch (normalized) {
      case 'text':
      case 'shorttext':
      case 'short_text':
        return 'Text (Short)';
      case 'longtext':
      case 'longtextinput':
      case 'long_text':
        return 'Text (Long)';
      case 'number':
      case 'numeric':
        return 'Number';
      case 'date':
        return 'Date';
      case 'daterange':
      case 'date_range':
      case 'date range':
        return 'Date Range';
      case 'dropdown':
        return 'Dropdown List';
      case 'list (single selection)':
      case 'single_select':
      case 'list-single':
      case 'list':
        return 'List (Single Selection)';
      case 'list (multiple selection)':
      case 'multiple_select':
      case 'multiselect':
      case 'list-multiple':
        return 'List (Multiple Selection)';
      case 'gps':
      case 'gps capture':
        return 'GPS Capture';
      case 'boolean':
      case 'toggle':
        return 'Boolean';
      case 'file':
      case 'file upload':
        return 'File Upload';
      case 'timestamp':
      case 'timestamp (auto)':
        return 'Timestamp (Auto)';
      case 'refnum':
      case 'reference number':
        return 'Reference Number';
      case 'duration':
        return 'Duration';
      default:
        return 'Text (Short)';
    }
  }

  // Reference Number, Timestamp (Auto) and Duration are auto-generated/computed at submit.
  private isAutoGeneratedType(type: string): boolean {
    const normalized = (type || '').toString().trim().toLowerCase();
    return ['refnum', 'reference number', 'timestamp', 'timestamp (auto)', 'duration'].includes(normalized);
  }

  private runtimeFieldListOptions(field: any, idMap: Map<string, number>): string {
    if (!field) return '';
    if (field.type === 'duration' || field.type === 'Duration') {
      const cfg = field.durationCfg || field.durationConfig || {};
      // A mirrored Duration field's Start/End sources live in the earlier stage
      // where the Duration field was originally configured, not in THIS stage's
      // field list, so idMap can't resolve them here. Fall back to a sentinel (no
      // dataPointId is ever 0) instead of the raw, non-numeric field id string —
      // a non-numeric listOptions makes log-sheet-form's isReferenceDuration()
      // return false, which renders blank self-contained date pickers instead of
      // the already-computed value carried in the mirrored field's own value.
      const start = idMap.get(cfg.startFieldId) ?? 0;
      const end = idMap.get(cfg.endFieldId) ?? 0;
      const unit = cfg.unit || 'days';
      return `${start},${end},${unit}`;
    }
    // RT-32: File Upload config (types,maxFiles,maxSizeMB) so the renderer enforces the
    // field's configured attachment limits instead of falling back to defaults.
    if (field.type === 'file' || field.type === 'File Upload') {
      const cfg = field.fileCfg || field.fileConfig || {};
      const types = cfg.types || 'any';
      const maxFiles = cfg.maxFiles ?? 5;
      const maxSizeMB = cfg.maxSizeMB ?? 25;
      return `${types},${maxFiles},${maxSizeMB}`;
    }
    // Reference Number config (prefix,padding) drives the generated value in the renderer.
    if (field.type === 'refnum' || field.type === 'Reference Number') {
      const cfg = field.refnumCfg || field.refnumConfig || {};
      const prefix = cfg.prefix ?? 'REF-';
      const padding = cfg.padding ?? 4;
      return `${prefix},${padding}`;
    }
    if (Array.isArray(field.options)) {
      return field.options.join(',');
    }
    if (typeof field.options === 'string') {
      return field.options;
    }
    return '';
  }

  private runtimeTriggerConditions(field: any, idMap: Map<string, number>): any {
    const conditions = field.triggerConditions || field.fieldCondition || null;
    if (!conditions) return null;
    const hiddenFieldDataPoint = conditions.hiddenFieldDataPoint ?? conditions.hiddenFieldId;
    return {
      ...conditions,
      hiddenFieldDataPoint: idMap.get(hiddenFieldDataPoint) ?? hiddenFieldDataPoint,
    };
  }

  // Per-field notes loaded from the item payload, re-hydrated into the rendered fields.
  private fieldNotes: Record<string, string> = {};

  private runtimeValues(workflow: WorkflowRuntimeInstance): Record<string, any> {
    const payload: any = workflow.payload;
    this.fieldNotes = payload && typeof payload === 'object' && payload.fieldNotes ? { ...payload.fieldNotes } : {};
    return {
      ...this.extractValues(workflow.payload),
    };
  }

  private extractValues(payload: any): Record<string, any> {
    if (!payload || typeof payload !== 'object') return {};
    return { ...(payload.fields && typeof payload.fields === 'object' ? payload.fields : payload) };
  }

  private resolveComputedValue(field: any): any {
    if (this.fieldType(field) !== 'duration') return '';
    return this.computeDuration(field) || '';
  }

  private computeDuration(field: any): string {
    const cfg = field.durationCfg || field.durationConfig || {};
    const start = this.values[cfg.startFieldId];
    const end = this.values[cfg.endFieldId];
    if (!start || !end) return '';
    const startDate = new Date(start);
    const endDate = new Date(end);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return '';
    const days = Math.round((endDate.getTime() - startDate.getTime()) / 86400000);
    return Number.isFinite(days) ? `${days}` : '';
  }

  private returnRoute(): string {
    return localStorage.getItem('role') === 'Operator' ? '/dashboard' : '/home';
  }
}
